import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface Invoice {
  id: string;
  company_id: string;
  invoice_number: string;
  amount_cents: number;
  currency: string;
  status: 'pending' | 'paid' | 'overdue' | 'cancelled';
  due_date: string;
  issue_date: string;
  payment_method?: 'card' | 'boleto' | 'pix' | 'manual';
  payment_id?: string;
  plan_ids: string[];
  period_start: string;
  period_end: string;
  metadata: any;
  boleto_url?: string;
  boleto_barcode?: string;
  pix_qr_code?: string;
  pix_copy_paste?: string;
  danfe_pdf_url?: string;
  xml_file_url?: string;
  fiscal_documents_uploaded_at?: string;
  fiscal_documents_uploaded_by?: string;
  created_at: string;
  updated_at: string;
  companies?: {
    name: string;
    doc_cnpj: string;
  };
}

export interface CreateInvoiceRequest {
  companyId: string;
  planIds: string[];
  amountCents: number;
  dueDate: string;
  periodStart: string;
  periodEnd: string;
  paymentMethod?: 'card' | 'boleto' | 'pix' | 'manual';
}

export const useInvoices = (companyId?: string, filters?: {
  status?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}) => {
  return useQuery({
    queryKey: ['invoices', companyId, filters],
    queryFn: async () => {
      let query = supabase
        .from('invoices')
        .select(`
          *,
          companies(name, doc_cnpj)
        `)
        .order('created_at', { ascending: false });

      // Only filter by company if provided
      if (companyId) {
        query = query.eq('company_id', companyId);
      }

      if (filters?.status) {
        query = query.eq('status', filters.status);
      }

      if (filters?.startDate) {
        query = query.gte('issue_date', filters.startDate);
      }

      if (filters?.endDate) {
        query = query.lte('issue_date', filters.endDate);
      }

      if (filters?.limit) {
        query = query.limit(filters.limit);
      }

      if (filters?.offset) {
        query = query.range(filters.offset, (filters.offset + (filters.limit || 10)) - 1);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      return data as any;
    },
  });
};

export const useInvoice = (invoiceId?: string) => {
  return useQuery({
    queryKey: ['invoice', invoiceId],
    queryFn: async () => {
      if (!invoiceId) return null;
      
      const { data, error } = await supabase
        .from('invoices')
        .select(`
          *,
          boleto_barcode,
          boleto_url,
          metadata,
          companies(name, doc_cnpj)
        `)
        .eq('id', invoiceId)
        .single();
      
      if (error && error.code !== 'PGRST116') throw error;
      return data as any;
    },
    enabled: !!invoiceId,
  });
};

export const useCreateInvoice = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (invoiceRequest: CreateInvoiceRequest) => {
      const { data, error } = await supabase.functions.invoke('billing-create-invoice', {
        body: invoiceRequest
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['invoices', variables.companyId] });
      toast({
        title: "Fatura Criada",
        description: "Fatura gerada com sucesso.",
      });
    },
    onError: (error: any) => {
      console.error('Error creating invoice:', error);
      toast({
        title: "Erro na Fatura",
        description: error.message || "Não foi possível criar a fatura.",
        variant: "destructive",
      });
    },
  });
};

export const useInvoiceStats = (companyId?: string) => {
  return useQuery({
    queryKey: ['invoice-stats', companyId],
    queryFn: async () => {
      let query = supabase
        .from('invoices')
        .select('status, amount_cents, due_date, created_at');
      
      // Only filter by company if provided
      if (companyId) {
        query = query.eq('company_id', companyId);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;

      const now = new Date();
      const stats = {
        total: data.length,
        totalAmount: data
          .filter(invoice => invoice.status === 'paid')
          .reduce((sum, invoice) => sum + invoice.amount_cents, 0),
        byStatus: data.reduce((acc, invoice) => {
          acc[invoice.status] = (acc[invoice.status] || 0) + 1;
          return acc;
        }, {} as Record<string, number>),
        overdue: data.filter(invoice => 
          invoice.status === 'pending' && new Date(invoice.due_date) < now
        ).length,
        thisMonth: data.filter(invoice => {
          const invoiceDate = new Date(invoice.created_at);
          return invoiceDate.getMonth() === now.getMonth() && 
                 invoiceDate.getFullYear() === now.getFullYear();
        }).length
      };

      return stats;
    },
  });
};

export const useDeleteInvoice = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (invoiceId: string) => {
      // Fetch invoice status before deleting
      const { data: invoice, error: fetchError } = await supabase
        .from('invoices')
        .select('status, invoice_number, company_id')
        .eq('id', invoiceId)
        .single();

      if (fetchError) throw fetchError;

      // Block deletion if status is 'paid'
      if (invoice.status === 'paid') {
        throw new Error('Não é possível excluir faturas pagas. As faturas pagas são permanentes para fins de auditoria.');
      }

      // Register deletion attempt in audit log
      const { data: { user } } = await supabase.auth.getUser();
      
      await supabase.from('audit_logs').insert({
        actor_user_id: user?.id || null,
        company_id: invoice.company_id,
        action: 'billing.invoice_delete',
        entity_type: 'invoice',
        entity_id: invoiceId,
        old_values: { 
          invoice_number: invoice.invoice_number, 
          status: invoice.status 
        },
        meta: { reason: 'manual_deletion' }
      });

      // Delete invoice
      const { error } = await supabase
        .from('invoices')
        .delete()
        .eq('id', invoiceId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      toast({
        title: "Fatura Deletada",
        description: "A fatura foi removida com sucesso.",
      });
    },
    onError: (error: any) => {
      console.error('Error deleting invoice:', error);
      toast({
        title: "Erro ao Deletar",
        description: error.message || "Não foi possível deletar a fatura.",
        variant: "destructive",
      });
    },
  });
};